import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, Moon, Sun, Compass, Info, X, Satellite,
  Navigation, Target, Eye, EyeOff
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * AR Constellation Overlay
 * Superimposes constellation lines and star labels on the live camera feed
 * Uses device orientation to match sky position
 */

// Constellation data with star positions for AR overlay
const AR_CONSTELLATIONS = {
  'Orion': {
    stars: [
      { name: 'Betelgeuse', ra: 88.79, dec: 7.41, magnitude: 0.5, color: '#ff6b4a' },
      { name: 'Rigel', ra: 78.63, dec: -8.20, magnitude: 0.1, color: '#a0c4ff' },
      { name: 'Bellatrix', ra: 81.28, dec: 6.35, magnitude: 1.6, color: '#c0d8ff' },
      { name: 'Saiph', ra: 86.94, dec: -9.67, magnitude: 2.1, color: '#c0d8ff' },
      { name: 'Alnitak', ra: 85.19, dec: -1.94, magnitude: 1.7, color: '#ffffff' },
      { name: 'Alnilam', ra: 84.05, dec: -1.20, magnitude: 1.7, color: '#ffffff' },
      { name: 'Mintaka', ra: 83.00, dec: -0.30, magnitude: 2.2, color: '#ffffff' },
    ],
    lines: [[0,2], [2,6], [6,5], [5,4], [4,0], [0,3], [3,1], [1,2]],
    mythology: 'The Hunter'
  },
  'Ursa Major': {
    stars: [
      { name: 'Dubhe', ra: 165.93, dec: 61.75, magnitude: 1.8, color: '#ffd27f' },
      { name: 'Merak', ra: 165.46, dec: 56.38, magnitude: 2.4, color: '#ffffff' },
      { name: 'Phecda', ra: 178.46, dec: 53.69, magnitude: 2.4, color: '#ffffff' },
      { name: 'Megrez', ra: 183.86, dec: 57.03, magnitude: 3.3, color: '#ffffff' },
      { name: 'Alioth', ra: 193.51, dec: 55.96, magnitude: 1.8, color: '#ffffff' },
      { name: 'Mizar', ra: 200.98, dec: 54.93, magnitude: 2.1, color: '#ffffff' },
      { name: 'Alkaid', ra: 206.88, dec: 49.31, magnitude: 1.9, color: '#c0d8ff' },
    ],
    lines: [[0,1], [1,2], [2,3], [3,4], [4,5], [5,6], [3,0]],
    mythology: 'The Great Bear (Big Dipper)'
  },
  'Cassiopeia': {
    stars: [
      { name: 'Schedar', ra: 10.13, dec: 56.54, magnitude: 2.2, color: '#ffd27f' },
      { name: 'Caph', ra: 2.29, dec: 59.15, magnitude: 2.3, color: '#ffffff' },
      { name: 'Gamma Cas', ra: 14.18, dec: 60.72, magnitude: 2.2, color: '#c0d8ff' },
      { name: 'Ruchbah', ra: 21.45, dec: 60.24, magnitude: 2.7, color: '#ffffff' },
      { name: 'Segin', ra: 28.60, dec: 63.67, magnitude: 3.4, color: '#c0d8ff' },
    ],
    lines: [[0,1], [1,2], [2,3], [3,4]],
    mythology: 'The Queen (W-shape)'
  },
  'Leo': {
    stars: [
      { name: 'Regulus', ra: 152.09, dec: 11.97, magnitude: 1.4, color: '#c0d8ff' },
      { name: 'Denebola', ra: 177.27, dec: 14.57, magnitude: 2.1, color: '#ffffff' },
      { name: 'Algieba', ra: 146.46, dec: 19.84, magnitude: 2.0, color: '#ffd27f' },
      { name: 'Zosma', ra: 168.53, dec: 20.52, magnitude: 2.6, color: '#ffffff' },
      { name: 'Chertan', ra: 168.56, dec: 15.43, magnitude: 3.3, color: '#ffffff' },
    ],
    lines: [[0,2], [2,3], [3,1], [3,4], [4,0]],
    mythology: 'The Lion'
  },
  'Scorpius': {
    stars: [
      { name: 'Antares', ra: 247.35, dec: -26.43, magnitude: 1.0, color: '#ff4444' },
      { name: 'Shaula', ra: 263.40, dec: -37.10, magnitude: 1.6, color: '#c0d8ff' },
      { name: 'Sargas', ra: 264.33, dec: -42.99, magnitude: 1.9, color: '#ffffff' },
      { name: 'Dschubba', ra: 240.08, dec: -22.62, magnitude: 2.3, color: '#c0d8ff' },
    ],
    lines: [[3,0], [0,1], [1,2]],
    mythology: 'The Scorpion'
  },
  'Cygnus': {
    stars: [
      { name: 'Deneb', ra: 310.36, dec: 45.28, magnitude: 1.3, color: '#ffffff' },
      { name: 'Sadr', ra: 305.56, dec: 40.26, magnitude: 2.2, color: '#ffd27f' },
      { name: 'Gienah', ra: 305.25, dec: 33.97, magnitude: 2.5, color: '#ffd27f' },
      { name: 'Delta Cyg', ra: 296.24, dec: 45.13, magnitude: 2.9, color: '#c0d8ff' },
      { name: 'Albireo', ra: 292.68, dec: 27.96, magnitude: 3.1, color: '#ffd27f' },
    ],
    lines: [[0,1], [1,4], [2,1], [1,3]],
    mythology: 'The Swan (Northern Cross)'
  }
};

// Planets with current approximate positions
const AR_PLANETS = [
  { name: 'Venus', color: '#ffd27f', size: 12 },
  { name: 'Mars', color: '#ff6b4a', size: 8 },
  { name: 'Jupiter', color: '#e8c59e', size: 16 },
  { name: 'Saturn', color: '#f4d59e', size: 14, hasRings: true },
];

const ARConstellationOverlay = ({ 
  enabled = true,
  showLabels = true,
  showLines = true,
  showPlanets = true,
  opacity = 0.8
}) => {
  const { theme } = useTheme();
  const canvasRef = useRef(null);
  const [deviceOrientation, setDeviceOrientation] = useState({ alpha: 0, beta: 0, gamma: 0 });
  const [hasPermission, setHasPermission] = useState(false);
  const [selectedStar, setSelectedStar] = useState(null);
  const animationRef = useRef(null);

  // Request device orientation permission (iOS 13+)
  const requestPermission = useCallback(async () => {
    if (typeof DeviceOrientationEvent !== 'undefined' && 
        typeof DeviceOrientationEvent.requestPermission === 'function') {
      try {
        const permission = await DeviceOrientationEvent.requestPermission();
        setHasPermission(permission === 'granted');
      } catch (e) {
        console.error('Orientation permission error:', e);
        setHasPermission(true); // Fallback for non-iOS
      }
    } else {
      setHasPermission(true); // Non-iOS devices
    }
  }, []);

  // Handle device orientation
  useEffect(() => {
    if (!enabled) return;

    const handleOrientation = (event) => {
      setDeviceOrientation({
        alpha: event.alpha || 0, // Compass direction (0-360)
        beta: event.beta || 0,   // Front-back tilt (-180 to 180)
        gamma: event.gamma || 0  // Left-right tilt (-90 to 90)
      });
    };

    if (hasPermission) {
      window.addEventListener('deviceorientation', handleOrientation, true);
    } else {
      requestPermission();
    }

    return () => {
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  }, [enabled, hasPermission, requestPermission]);

  // Convert RA/Dec to screen coordinates based on device orientation
  const celestialToScreen = useCallback((ra, dec, width, height) => {
    // Simplified projection - in real app would use proper spherical projection
    const { alpha, beta } = deviceOrientation;
    
    // Normalize RA to device heading
    const adjustedRA = (ra - alpha + 360) % 360;
    
    // Convert to screen coordinates
    const x = ((adjustedRA - 180) / 180) * width + width / 2;
    const y = ((90 - dec - beta) / 90) * height / 2 + height / 4;
    
    // Check if in view
    const inView = adjustedRA > 90 && adjustedRA < 270 && 
                   dec > beta - 45 && dec < beta + 45;
    
    return { x, y, inView };
  }, [deviceOrientation]);

  // Draw AR overlay
  useEffect(() => {
    if (!canvasRef.current || !enabled) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    const draw = () => {
      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw constellations
      Object.entries(AR_CONSTELLATIONS).forEach(([name, constellation]) => {
        const screenStars = constellation.stars.map(star => ({
          ...star,
          screen: celestialToScreen(star.ra, star.dec, width, height)
        }));

        // Check if any stars are in view
        const visibleStars = screenStars.filter(s => s.screen.inView);
        if (visibleStars.length === 0) return;

        // Draw constellation lines
        if (showLines) {
          ctx.strokeStyle = `${theme.colors.primary}${Math.round(opacity * 100).toString(16).padStart(2, '0')}`;
          ctx.lineWidth = 1.5;
          ctx.setLineDash([5, 5]);

          constellation.lines.forEach(([from, to]) => {
            const star1 = screenStars[from];
            const star2 = screenStars[to];
            
            if (star1.screen.inView || star2.screen.inView) {
              ctx.beginPath();
              ctx.moveTo(star1.screen.x, star1.screen.y);
              ctx.lineTo(star2.screen.x, star2.screen.y);
              ctx.stroke();
            }
          });
          ctx.setLineDash([]);
        }

        // Draw stars
        screenStars.forEach(star => {
          if (!star.screen.inView) return;

          const { x, y } = star.screen;
          const size = Math.max(3, 6 - star.magnitude) * 1.5;

          // Star glow
          const gradient = ctx.createRadialGradient(x, y, 0, x, y, size * 3);
          gradient.addColorStop(0, star.color + 'cc');
          gradient.addColorStop(0.5, star.color + '44');
          gradient.addColorStop(1, 'transparent');
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(x, y, size * 3, 0, Math.PI * 2);
          ctx.fill();

          // Star core
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fillStyle = star.color;
          ctx.fill();

          // Star label
          if (showLabels && star.magnitude < 2) {
            ctx.font = '11px monospace';
            ctx.fillStyle = `rgba(255,255,255,${opacity})`;
            ctx.textAlign = 'center';
            ctx.fillText(star.name, x, y - size - 8);
          }
        });

        // Draw constellation name
        if (showLabels && visibleStars.length > 2) {
          const avgX = visibleStars.reduce((sum, s) => sum + s.screen.x, 0) / visibleStars.length;
          const avgY = visibleStars.reduce((sum, s) => sum + s.screen.y, 0) / visibleStars.length;
          
          ctx.font = 'bold 14px sans-serif';
          ctx.fillStyle = theme.colors.primary;
          ctx.textAlign = 'center';
          ctx.fillText(name, avgX, avgY - 30);
          
          ctx.font = '10px sans-serif';
          ctx.fillStyle = `rgba(255,255,255,${opacity * 0.7})`;
          ctx.fillText(constellation.mythology, avgX, avgY - 16);
        }
      });

      // Draw planets
      if (showPlanets) {
        AR_PLANETS.forEach((planet, index) => {
          // Simulate planet positions (in real app would use ephemeris data)
          const ra = (index * 60 + Date.now() / 100000) % 360;
          const dec = Math.sin(Date.now() / 50000 + index) * 20;
          const screen = celestialToScreen(ra, dec, width, height);
          
          if (!screen.inView) return;

          const { x, y } = screen;

          // Planet glow
          const gradient = ctx.createRadialGradient(x, y, 0, x, y, planet.size * 2);
          gradient.addColorStop(0, planet.color + 'cc');
          gradient.addColorStop(0.5, planet.color + '44');
          gradient.addColorStop(1, 'transparent');
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(x, y, planet.size * 2, 0, Math.PI * 2);
          ctx.fill();

          // Planet body
          ctx.beginPath();
          ctx.arc(x, y, planet.size, 0, Math.PI * 2);
          ctx.fillStyle = planet.color;
          ctx.fill();

          // Saturn's rings
          if (planet.hasRings) {
            ctx.beginPath();
            ctx.ellipse(x, y, planet.size * 1.8, planet.size * 0.4, 0.3, 0, Math.PI * 2);
            ctx.strokeStyle = planet.color + '88';
            ctx.lineWidth = 2;
            ctx.stroke();
          }

          // Planet label
          if (showLabels) {
            ctx.font = 'bold 12px sans-serif';
            ctx.fillStyle = planet.color;
            ctx.textAlign = 'center';
            ctx.fillText(planet.name, x, y - planet.size - 10);
          }
        });
      }

      // Draw compass
      ctx.save();
      ctx.translate(width - 50, 50);
      
      // Compass circle
      ctx.beginPath();
      ctx.arc(0, 0, 30, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fill();
      ctx.strokeStyle = theme.colors.primary + '80';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Compass needle
      const compassAngle = (-deviceOrientation.alpha * Math.PI / 180);
      ctx.rotate(compassAngle);
      
      ctx.beginPath();
      ctx.moveTo(0, -20);
      ctx.lineTo(5, 10);
      ctx.lineTo(-5, 10);
      ctx.closePath();
      ctx.fillStyle = '#ff4444';
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(0, 20);
      ctx.lineTo(5, -10);
      ctx.lineTo(-5, -10);
      ctx.closePath();
      ctx.fillStyle = '#ffffff';
      ctx.fill();

      ctx.restore();

      // Draw N marker
      ctx.font = 'bold 10px sans-serif';
      ctx.fillStyle = '#ff4444';
      ctx.textAlign = 'center';
      ctx.fillText('N', width - 50, 15);

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [enabled, showLabels, showLines, showPlanets, opacity, celestialToScreen, theme]);

  if (!enabled) return null;

  return (
    <canvas
      ref={canvasRef}
      width={window.innerWidth}
      height={window.innerHeight}
      className="absolute inset-0 pointer-events-none z-20"
      style={{ opacity }}
    />
  );
};

// AR Controls component
export const ARControls = ({
  arEnabled,
  setArEnabled,
  showLabels,
  setShowLabels,
  showLines,
  setShowLines,
  showPlanets,
  setShowPlanets
}) => {
  const { theme } = useTheme();

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="absolute top-4 right-4 z-30 flex flex-col gap-2"
    >
      {/* AR Toggle */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setArEnabled(!arEnabled)}
        className="p-3 rounded-2xl transition-all"
        style={{
          background: arEnabled 
            ? `linear-gradient(135deg, ${theme.colors.primary}40, ${theme.colors.accent}30)` 
            : 'rgba(0,0,0,0.5)',
          border: `2px solid ${arEnabled ? theme.colors.primary : 'rgba(255,255,255,0.2)'}`,
          boxShadow: arEnabled ? `0 0 20px ${theme.colors.primary}40` : 'none'
        }}
        data-testid="ar-toggle-btn"
      >
        {arEnabled ? (
          <Eye className="w-5 h-5" style={{ color: theme.colors.primary }} />
        ) : (
          <EyeOff className="w-5 h-5 text-slate-400" />
        )}
      </motion.button>

      {/* AR Options (show when AR is enabled) */}
      <AnimatePresence>
        {arEnabled && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex flex-col gap-2"
          >
            <button
              onClick={() => setShowLabels(!showLabels)}
              className={`p-2 rounded-xl text-xs font-bold ${showLabels ? 'bg-white/20' : 'bg-black/50'}`}
              style={{ color: showLabels ? theme.colors.primary : '#888' }}
            >
              Labels
            </button>
            <button
              onClick={() => setShowLines(!showLines)}
              className={`p-2 rounded-xl text-xs font-bold ${showLines ? 'bg-white/20' : 'bg-black/50'}`}
              style={{ color: showLines ? theme.colors.primary : '#888' }}
            >
              Lines
            </button>
            <button
              onClick={() => setShowPlanets(!showPlanets)}
              className={`p-2 rounded-xl text-xs font-bold ${showPlanets ? 'bg-white/20' : 'bg-black/50'}`}
              style={{ color: showPlanets ? theme.colors.primary : '#888' }}
            >
              Planets
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ARConstellationOverlay;
